import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Commodity, IClasificacionPunto, IPuntoMedicion } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { HelperService } from '../../../auxiliares/helper.service';
import {
  ClasificacionService,
  ICatalogoClasificacion,
  IResultadoBulkClasificacion,
} from '../service';
import { etiquetaCodigo } from '../etiquetas';

export interface IDatosEditarClasificacion {
  /** Uno = edición del punto; varios = clasificación masiva con el mismo valor. */
  puntos: IPuntoMedicion[];
}

/**
 * Etiquetas de los servicios.
 *
 * Es un `Record<Commodity, string>` a propósito: si `modelos` agrega una
 * vertical, este mapa deja de compilar y hay que decidir cómo se llama, en vez
 * de que la vertical nueva desaparezca en silencio del selector. El tipo hace de
 * sincronización sin traerse el schema de Zod al bundle del navegador.
 */
const ETIQUETAS_COMMODITY: Record<Commodity, string> = {
  gas: 'Gas',
  agua: 'Agua',
  electricidad: 'Electricidad',
  saneamiento: 'Saneamiento',
  otro: 'Otro',
  na: 'No aplica',
};

/**
 * Editor de la clasificación de un punto, y de varios a la vez.
 *
 * Dos cosas que el formulario tiene que dejar explícitas porque son del dominio
 * y no se ven en la pantalla:
 *
 * 1. **La escritura REEMPLAZA la clasificación entera.** El backend hace `$set`
 *    del objeto completo, no un merge campo a campo. En la edición de un punto
 *    eso es inocuo porque el formulario viene precargado con lo que ya tenía; en
 *    la masiva NO, y por eso se avisa cuántos de los seleccionados ya tienen algo.
 * 2. **Todo lo que se guarda desde acá queda bloqueado** para el clasificador
 *    automático. Lo fuerza el backend. Es la garantía de que la corrección
 *    sobrevive a la corrida de esa noche.
 */
@Component({
  selector: 'app-editar-clasificacion',
  templateUrl: './editar-clasificacion.component.html',
  styleUrls: ['./editar-clasificacion.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditarClasificacionComponent implements OnInit {
  public form?: UntypedFormGroup;
  public enviando = false;

  public readonly puntos: IPuntoMedicion[];
  public readonly esMasiva: boolean;
  /** Cuántos de los seleccionados ya tienen clasificación y se van a reemplazar. */
  public readonly yaClasificados: number;

  public readonly commodities = Object.keys(ETIQUETAS_COMMODITY) as Commodity[];

  public catalogo?: ICatalogoClasificacion;
  public cargandoCatalogo = false;

  /** Fallidos de la última masiva, para mostrarlos en vez de un "listo" genérico. */
  public resultadoBulk?: IResultadoBulkClasificacion;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IDatosEditarClasificacion,
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<EditarClasificacionComponent>,
    private service: ClasificacionService,
    private helper: HelperService,
  ) {
    this.puntos = data?.puntos ?? [];
    this.esMasiva = this.puntos.length > 1;
    this.yaClasificados = this.puntos.filter((p) =>
      this.tieneAporte(p.clasificacion),
    ).length;
  }

  get title(): string {
    return this.esMasiva
      ? `Clasificar ${this.puntos.length} puntos`
      : `Clasificar ${this.puntos[0]?.nombre ?? 'punto'}`;
  }

  /**
   * Mismo criterio que usa el backend para contar cobertura: el servicio solo no
   * cuenta como clasificación, porque se deriva de la división y lo tendrían todos.
   */
  private tieneAporte(c?: IClasificacionPunto): boolean {
    return !!(
      c &&
      (c.tipoInstalacion ||
        c.rolesRed?.length ||
        c.subrol ||
        c.segmentoUsuario ||
        c.nivelRed)
    );
  }

  public etiquetaCommodity(c: Commodity): string {
    return ETIQUETAS_COMMODITY[c] ?? c;
  }

  public etiquetaCodigo = etiquetaCodigo;

  private createForm(): void {
    const c: IClasificacionPunto = this.esMasiva
      ? {}
      : (this.puntos[0]?.clasificacion ?? {});

    this.form = this.fb.group({
      // `commodity` es campo propio de la clasificación, no derivado del nivel de
      // red: los tres ejes de catálogo se validan contra el catálogo de ESTE
      // servicio, así que hace falta saberlo para poder escribirlos.
      commodity: [c.commodity ?? c.nivelRed?.commodity ?? null],
      tipoInstalacion: [c.tipoInstalacion ?? null],
      rolesRed: [c.rolesRed ?? []],
      subrol: [c.subrol ?? null],
      // Tri-estado a propósito: "no sé" no es lo mismo que "no es bidireccional".
      // Ausente o false significa que una lectura en sentido inverso es una FALLA.
      bidireccional: [c.bidireccional ?? null],
      contraparte: [c.contraparte ?? null],
      nivelRedCodigo: [c.nivelRed?.codigo ?? null],
      segmentoUsuario: [c.segmentoUsuario ?? null],
      claseTrazado: [c.claseTrazado ?? null],
    });

    this.form.get('commodity')!.valueChanges.subscribe((commodity) => {
      this.onCommodityChange(commodity);
    });
  }

  /**
   * Al cambiar el servicio se limpian los ejes de catálogo: un subrol eléctrico
   * en un punto de gas lo rechaza el backend con 400, y dejarlo puesto haría que
   * el operador vea un valor que no se puede guardar.
   */
  private async onCommodityChange(commodity: Commodity | null): Promise<void> {
    this.form?.patchValue(
      {
        tipoInstalacion: null,
        subrol: null,
        segmentoUsuario: null,
        nivelRedCodigo: null,
        // La clase de trazado es de NAG-100: sólo aplica a gas.
        claseTrazado: commodity === 'gas' ? this.form.value.claseTrazado : null,
      },
      { emitEvent: false },
    );
    await this.cargarCatalogo(commodity);
  }

  private async cargarCatalogo(commodity: Commodity | null): Promise<void> {
    this.catalogo = undefined;
    if (!commodity) return;
    try {
      this.cargandoCatalogo = true;
      this.catalogo = await firstValueFrom(
        this.service.getCatalogos(commodity),
      );
    } catch (err) {
      this.helper.notifError(err);
    } finally {
      this.cargandoCatalogo = false;
    }
  }

  get esGas(): boolean {
    return this.form?.value.commodity === 'gas';
  }

  /** El servicio elegido no tiene catálogo definido (por ejemplo `otro` o `na`). */
  get catalogoVacio(): boolean {
    if (!this.catalogo) return false;
    return (
      !this.catalogo.nivelesRed.length &&
      !this.catalogo.tiposInstalacion.length &&
      !this.catalogo.subroles.length &&
      !this.catalogo.segmentosUsuario.length
    );
  }

  /**
   * Arma el `IClasificacionPunto` a guardar, omitiendo lo vacío.
   *
   * Omitir y no mandar `null` importa: `rolesRed: []` lo rechaza el backend
   * ("omitilo si no se sabe"), y un `undefined` explícito borraría el campo igual
   * porque la escritura reemplaza el objeto entero.
   */
  private getClasificacion(): IClasificacionPunto {
    const v = this.form!.value;
    const c: IClasificacionPunto = {};

    if (v.commodity) c.commodity = v.commodity;
    if (v.tipoInstalacion) c.tipoInstalacion = v.tipoInstalacion;
    if (v.rolesRed?.length) c.rolesRed = v.rolesRed;
    if (v.subrol) c.subrol = v.subrol;
    if (v.bidireccional !== null && v.bidireccional !== undefined) {
      c.bidireccional = v.bidireccional;
    }
    const contraparte = (v.contraparte ?? '').trim();
    if (contraparte) c.contraparte = contraparte;
    if (v.segmentoUsuario) c.segmentoUsuario = v.segmentoUsuario;

    if (v.nivelRedCodigo && v.commodity) {
      const nivel = this.catalogo?.nivelesRed.find(
        (n) => n.codigo === v.nivelRedCodigo,
      );
      if (nivel) {
        // `orden` se copia del catálogo: es propiedad del catálogo y el backend
        // rechaza el que no coincida con el código.
        c.nivelRed = {
          commodity: v.commodity,
          codigo: nivel.codigo,
          orden: nivel.orden,
        };
      }
    }

    if (v.claseTrazado && v.commodity === 'gas') {
      c.claseTrazado = Number(v.claseTrazado);
    }

    return c;
  }

  /**
   * Guardar sólo el servicio no cuenta como clasificar: es lo que el clasificador
   * ya deriva de la división y el backend no lo suma a la cobertura. Sin este
   * corte, el operador cree que resolvió el punto y sigue apareciendo en la lista.
   */
  get aportaAlgo(): boolean {
    if (!this.form) return false;
    const v = this.form.value;
    return !!(
      v.tipoInstalacion ||
      v.rolesRed?.length ||
      v.subrol ||
      v.segmentoUsuario ||
      v.nivelRedCodigo
    );
  }

  public async onSubmit(): Promise<void> {
    if (!this.aportaAlgo || this.enviando) return;
    const clasificacion = this.getClasificacion();
    this.resultadoBulk = undefined;

    try {
      this.enviando = true;
      if (this.esMasiva) {
        const ids = this.puntos.map((p) => p._id!).filter(Boolean);
        const resultado = await firstValueFrom(
          this.service.clasificarBulk(ids, clasificacion),
        );
        this.resultadoBulk = resultado;
        if (resultado.fallidos.length) {
          // No se cierra: los fallidos son el resultado y el operador tiene que
          // poder verlos. Lo más común es que el punto esté fuera de su alcance.
          this.helper.notifWarn(
            `${resultado.ok} de ${resultado.total} clasificados. ${resultado.fallidos.length} fallaron.`,
          );
        } else {
          this.helper.notifSuccess(`${resultado.ok} puntos clasificados`);
          this.dialogRef.close(true);
        }
      } else {
        await firstValueFrom(
          this.service.clasificar(this.puntos[0]._id!, clasificacion),
        );
        this.helper.notifSuccess('Clasificación guardada');
        this.dialogRef.close(true);
      }
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    } finally {
      this.enviando = false;
    }
  }

  public nombrePunto(id: string): string {
    return this.puntos.find((p) => p._id === id)?.nombre ?? id;
  }

  public close(): void {
    // Si hubo una masiva parcial, el listado de atrás quedó desactualizado.
    this.dialogRef.close(!!this.resultadoBulk?.ok);
  }

  async ngOnInit(): Promise<void> {
    this.createForm();
    await this.cargarCatalogo(this.form!.value.commodity);
  }
}
